import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CreateRole, GetSingleRole, UpdateRole, GetAllPermissions } from '../../../../Api/role';
import { ErrorToast, SuccessToast } from '../../../../Utils/SweetAlert';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import InputWithLabel from '../../../../Components/InputWithLabel';
import { BackButton } from '../../../../Components';
import SaveButton from '../../../../Components/SaveButton';

const validationSchema = Yup.object({
    name: Yup.string().trim().required('Name is required.'),
    permissions: Yup.array().of(Yup.string()).min(1, 'Select at least 1 permission.').required('Permissions are required.')
});

const AddEdit = ({ mode = 'create' }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const id = location?.state?.id;
    const [isLoading, setIsLoading] = React.useState(false);

    const { data: singleRes } = useQuery({
        enabled: mode === 'edit' && !!id,
        queryKey: ['get-role-single', id],
        queryFn: () => GetSingleRole({ Id: id }),
        throwOnError: (error) => ErrorToast(error?.response?.data?.message || 'Server Error.'),
    });

    const { data: permsRes, isFetching: isPermsLoading } = useQuery({
        queryKey: ['get-role-permissions'],
        queryFn: () => GetAllPermissions(),
        throwOnError: (error) => ErrorToast(error?.response?.data?.message || 'Server Error.'),
    });

    const createMut = useMutation({
        mutationFn: CreateRole,
        onSuccess: (res) => {
            if (res?.status === 1) {
                SuccessToast(res?.message || 'Created successfully');
                navigate('/admin/role-listing');
            } else {
                ErrorToast(res?.message || 'Server Error.');
            }
        },
        onError: (error) => {
            ErrorToast(error?.response?.data?.message || 'Server Error.');
            setIsLoading(false);
        },
        onSettled: () => setIsLoading(false),
    });

    const updateMut = useMutation({
        mutationFn: UpdateRole,
        onSuccess: (res) => {
            if (res?.status === 1) {
                SuccessToast(res?.message || 'Updated successfully');
                navigate('/admin/role-listing');
            } else {
                ErrorToast(res?.message || 'Server Error.');
            }
        },
        onError: (error) => {
            ErrorToast(error?.response?.data?.message || 'Server Error.');
            setIsLoading(false);
        },
        onSettled: () => setIsLoading(false),
    });

    const roleData = singleRes?.data?.data || {};
    const allPermissions = permsRes?.data?.rows || [];

    const permissionsByModel = useMemo(() => {
        const grouped = {};
        (allPermissions || []).forEach((p) => {
            const key = p?.modelName || 'Other';
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(p);
        });
        return grouped;
    }, [allPermissions]);

    const allPermissionIds = useMemo(() => (allPermissions || []).map((p) => String(p?._id)), [allPermissions]);

    const initialValues = {
        name: mode === 'edit' ? roleData?.name || '' : '',
        permissions: mode === 'edit' ? (roleData?.permissions || []).map(String) : [],
    };

    return (
        <>
            <Helmet>
                <title>OVE | {mode === 'edit' ? 'Edit Role' : 'Add Role'}</title>
            </Helmet>
            <div className='page-wrapper'>
                <div className='content container-fluid'>
                    <div className='row'>
                        <div className='col-md-12'>
                            <div className='card card-border overflow-visible'>
                                <div className='card-body'>
                                    <div className='page-header d-block'>
                                        <div className='row'>
                                            <div className='col'>
                                                <h3 className='page-title'>{mode === 'edit' ? 'Edit Role' : 'Add Role'}</h3>
                                            </div>
                                            <div className='col-auto d-flex align-items-center justify-content-center'>
                                                <BackButton to='/admin/role-listing' />
                                            </div>
                                        </div>
                                    </div>

                                    <section className='section-border'>
                                        <Formik
                                            enableReinitialize
                                            initialValues={initialValues}
                                            validationSchema={validationSchema}
                                            onSubmit={(values) => {
                                                setIsLoading(true);
                                                const payload = {
                                                    name: values.name,
                                                    permissions: values.permissions,
                                                };
                                                if (mode === 'edit') {
                                                    updateMut.mutate({ id, ...payload });
                                                } else {
                                                    createMut.mutate(payload);
                                                }
                                            }}
                                        >
                                            {({ values, setFieldValue, errors, touched }) => {
                                                const allSelected = allPermissionIds.length > 0 && values.permissions.length === allPermissionIds.length;

                                                const toggleAll = () => {
                                                    if (allSelected) {
                                                        setFieldValue('permissions', []);
                                                    } else {
                                                        setFieldValue('permissions', allPermissionIds);
                                                    }
                                                };

                                                const toggleGroup = (group) => {
                                                    const groupIds = (permissionsByModel[group] || []).map((p) => String(p?._id));
                                                    const set = new Set(values.permissions);
                                                    const allInGroupSelected = groupIds.every((id) => set.has(id));
                                                    if (allInGroupSelected) {
                                                        groupIds.forEach((id) => set.delete(id));
                                                    } else {
                                                        groupIds.forEach((id) => set.add(id));
                                                    }
                                                    setFieldValue('permissions', Array.from(set));
                                                };

                                                return (
                                                    <Form>
                                                        <div className='row gy-4'>
                                                            <div className='col-md-6'>
                                                                <Field
                                                                    Label='Name'
                                                                    type='text'
                                                                    name='name'
                                                                    placeholder='Enter role name'
                                                                    require
                                                                    component={InputWithLabel}
                                                                />
                                                            </div>

                                                            <div className='col-md-12'>
                                                                <div className='d-flex justify-content-between align-items-center mb-2'>
                                                                    <label className='form-label m-0'>Permissions <span className='text-danger'>*</span></label>
                                                                    <button type='button' className='btn btn-sm btn-outline-primary primary-button' onClick={toggleAll}>
                                                                        {allSelected ? 'Deselect All' : 'Select All'}
                                                                    </button>
                                                                </div>
                                                                {touched.permissions && errors.permissions && (
                                                                    <div className='text-danger mb-2'>{errors.permissions}</div>
                                                                )}
                                                                {isPermsLoading && <div>Loading permissions...</div>}
                                                                {!isPermsLoading && (
                                                                    <div className='row'>
                                                                        {Object.keys(permissionsByModel).map((group) => {
                                                                            const groupIds = (permissionsByModel[group] || []).map((p) => String(p?._id));
                                                                            const allInGroupSelected = groupIds.length > 0 && groupIds.every((id) => values.permissions.includes(id));
                                                                            return (
                                                                                <div className='col-md-4 mb-3' key={group}>
                                                                                    <div className='card h-100'>
                                                                                        <div className='card-header gap-2' style={{ padding: "10px 0px 10px 30px", }}>
                                                                                            <input
                                                                                                type='checkbox'
                                                                                                className='form-check-input'
                                                                                                checked={allInGroupSelected}
                                                                                                onChange={() => toggleGroup(group)}
                                                                                            />
                                                                                            <strong className=''>{group}</strong>
                                                                                        </div>
                                                                                        <div className='card-body' style={{ paddingLeft: 12 }}>
                                                                                            {permissionsByModel[group].map((perm) => {
                                                                                                const permId = String(perm?._id);
                                                                                                const checked = values.permissions.includes(permId);
                                                                                                return (
                                                                                                    <div className='form-check d-flex align-items-center gap-2' key={permId}>
                                                                                                        <input
                                                                                                            className='form-check-input'
                                                                                                            type='checkbox'
                                                                                                            id={`perm-${permId}`}
                                                                                                            checked={checked}
                                                                                                            onChange={(e) => {
                                                                                                                const next = new Set(values.permissions);
                                                                                                                if (e.target.checked) {
                                                                                                                    next.add(permId);
                                                                                                                } else {
                                                                                                                    next.delete(permId);
                                                                                                                }
                                                                                                                setFieldValue('permissions', Array.from(next));
                                                                                                            }}
                                                                                                        />
                                                                                                        <div className='form-check-label' htmlFor={`perm-${permId}`}>
                                                                                                            {perm?.name}
                                                                                                        </div>
                                                                                                    </div>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <SaveButton loading={isLoading} />
                                                    </Form>
                                                );
                                            }}
                                        </Formik>
                                    </section>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddEdit;


